import Typesense from 'typesense';
// config
import { TYPESENSE_CONFIG } from 'src/config-global';
// utils
import { RESPONSE_STATUS_SUCCESS } from 'src/constant';
import { findCRMValueByCRMKey } from 'src/utils/crm-commons';
// stores
import { useCRMRecordStore } from 'src/stores/useCRMRecordStore';

const typesenseClient = new Typesense.Client({
  nodes: [
    {
      host: TYPESENSE_CONFIG.host,
      port: 443,
      protocol: 'https',
    }
  ],
  apiKey: TYPESENSE_CONFIG.api_key,
  connectionTimeoutSeconds: 5
});

const typesenseCRMRecordsCollection = typesenseClient.collections('crm_records').documents();
const typesenseCRMActivityCollection = typesenseClient.collections('crm_activity').documents();
const typesenseScheduledActionsCollection = typesenseClient.collections('scheduled_actions').documents();

export const typesenseSearchCRMRecords = async (organization_id, record_type, pagination, query, query_by, filter_by, sort_by) => {
  try {
    const filterRecordType = record_type === 'all' ? '' : `&&record_type:=${record_type}`;
    const sortBy = sort_by ? sort_by : 'sort_timestamp:desc';
    const filterBy = `organization_id:=${organization_id}&&status:=active${filterRecordType}${filter_by}`;
    const searchParameters = {
      q: query,
      filter_by: filterBy,
      sort_by: sortBy,
      page: pagination.page + 1,
      per_page: pagination.pageSize,
      highlight: false,
      exclude_fields: 'organization_id,status',
    };
    if(query_by) {
      searchParameters.query_by = query_by;
      searchParameters.infix = 'always';
    }
    const { found, hits, page } = await typesenseCRMRecordsCollection.search(searchParameters);
    if(record_type !== 'all' && found > 0 && !pagination.page) {
      const idSearchParameters = {
        q: query,
        filter_by: filterBy,
        sort_by: sortBy,
        per_page: 100,
        highlight: false,
        include_fields: 'id',
      };
      if(query_by) {
        idSearchParameters.query_by = query_by;
        idSearchParameters.infix = 'always';
      }
      const totalPages = Math.ceil(found / 100);
      const idRequests = [];
      for(let currentPage = 1; currentPage <= totalPages; currentPage += 1) {
        idRequests.push(typesenseCRMRecordsCollection.search({ ...idSearchParameters, page: currentPage }));
      }
      const idResponses = await Promise.all(idRequests);
      const allIds = [ ...new Set(idResponses.flatMap(response => response.hits.map(item => item.document.id))) ];
      const find = findCRMValueByCRMKey('value', record_type);
      useCRMRecordStore.getState().setCRMRecordsIds({ records: allIds, recordType: find.pluralLowerCase });
    }
    const mapHits = hits.map(item => ({
      ...item.document
    }));
    return { status: RESPONSE_STATUS_SUCCESS, documents: mapHits, found, page };
  } catch (error) {
    return { status: 'error', message: 'Typesense Error' };
  }
};

export const typesenseSearchAllCRMRecords = async (organization_id, record_type, query, query_by, filter_by, sort_by, per_page = 200) => {
  try {
    const sortBy = sort_by ? sort_by : 'sort_timestamp:desc';
    const filterBy = `organization_id:=${organization_id}&&status:=active&&record_type:=${record_type}${filter_by}`;
    const searchParameters = {
      q: query,
      filter_by: filterBy,
      sort_by: sortBy,
      per_page,
      highlight: false,
      exclude_fields: 'organization_id,status',
    };
    if(query_by) {
      searchParameters.query_by = query_by;
      searchParameters.infix = 'always';
    }
    const { found, hits } = await typesenseCRMRecordsCollection.search({ ...searchParameters, page: 1 });
    const documents = hits.map(item => ({ ...item.document }));
    const totalPages = Math.ceil(found / per_page);
    const requests = [];
    for(let currentPage = 2; currentPage <= totalPages; currentPage += 1) {
      requests.push(typesenseCRMRecordsCollection.search({ ...searchParameters, page: currentPage }));
    }
    const responses = await Promise.all(requests);
    for(const response of responses) {
      documents.push(...response.hits.map(item => ({ ...item.document })));
    }
    return { status: RESPONSE_STATUS_SUCCESS, documents, found };
  } catch (error) {
    return { status: 'error', message: 'Typesense Error' };
  }
};

export const typesenseGroupByActivityCategoryCRMActivity = async (organization_id, filter_by) => {
  try {
    const searchParameters = {
      q: '*',
      query_by: 'created_by_category',
      filter_by: `organization_id:=${organization_id}&&active_record:=true&&status:=active${filter_by}`,
      facet_by: 'created_by_category',
      include_fields: 'none',
      max_facet_values: 500,
    };
    const { facet_counts } = await typesenseCRMActivityCollection.search(searchParameters);
    return { status: RESPONSE_STATUS_SUCCESS, documents: facet_counts.length ? facet_counts[0].counts : [] };
  } catch (error) {
    return { status: 'error', message: 'Typesense Error' };
  }
};

export const typesenseSearchCRMActivities = async (organization_id, pagination, query, filter_by, query_by) => {
  try {
    const searchParameters = {
      q: query,
      filter_by: `organization_id:=${organization_id}&&active_record:=true&&status:=active&&is_visible:=true${filter_by}`,
      sort_by: 'updated_at:desc',
      page: pagination.page + 1,
      per_page: pagination.pageSize,
      highlight: false,
      exclude_fields: 'active_record,created_by_category,is_visible,organization_id,owner_id_record_type,status',
    };
    if(query_by) {
      searchParameters.query_by = query_by;
      searchParameters.infix = 'always';
    }
    const { found, hits, page } = await typesenseCRMActivityCollection.search(searchParameters);
    const documents = [];
    if(hits.length) {
      const uniqueIds = [...new Set(hits.map(item => item.document.record_id))];
      const searchParameters = {
        q: '*',
        filter_by: `id:=[${uniqueIds.join(',')}]`,
        per_page: uniqueIds.length,
        highlight: false,
        include_fields: 'id,avatar_url',
      };
      const { hits: recordHits } = await typesenseCRMRecordsCollection.search(searchParameters);
      for(const value of hits) {
        const find = recordHits.find(record => record.document.id === value.document.record_id).document;
        documents.push({
          ...value.document,
          avatar_url: find.avatar_url,
        });
      }
    }
    return { status: RESPONSE_STATUS_SUCCESS, documents, found, page };
  } catch (error) {
    return { status: 'error', message: 'Typesense Error' };
  }
};

export const typesenseSearchScheduledActions = async (organization_id, timestamp_start, timestamp_end) => {
  try {
    const searchParameters = {
      q: '*',
      filter_by: `organization_id:=${organization_id}&&active_record:=true&&status:=active&&scheduled_at:[${timestamp_start}..${timestamp_end}]`,
      sort_by: 'scheduled_at:asc',
      per_page: 250,
      highlight: false,
      include_fields: 'id,action_type,description,owner_id,priority,record_id,record_name,record_type,scheduled_at,scheduled_action_status,title',
    };
    const { found, hits } = await typesenseScheduledActionsCollection.search(searchParameters);
    const documents = hits.map(item => ({ ...item.document }));
    return { status: RESPONSE_STATUS_SUCCESS, documents, found };
  } catch (error) {
    return { status: 'error', message: 'Typesense Error' };
  }
};
